import React, { useContext } from "react";
import { Button } from "@kui/react";
import { classNames } from "components/utils";
import { OneClickDeployContext } from "contexts/OneClickDeployContext";
import Spinner from "components/UI-lib/Spinner";

interface LaunchableDeployButtonProps {
  loading?: boolean;
  instanceLoading: boolean;
  isPublic?: boolean;
  nameErrorMessage: string;
  onDeploy: () => void;
  deployLabel?: string;
  estimatedCost?: React.ReactNode;
  disclosure?: React.ReactNode;
  compact?: boolean;
}

export const LaunchableDeployButton: React.FC<LaunchableDeployButtonProps> = ({
  loading = false,
  instanceLoading,
  isPublic,
  nameErrorMessage,
  onDeploy,
  deployLabel = "Deploy Launchable",
  estimatedCost,
  disclosure,
  compact = false,
}) => {
  const { initLoading } = useContext(OneClickDeployContext);
  const isDisabled =
    initLoading || loading || instanceLoading || !!nameErrorMessage;

  return (
    <div
      className={classNames(
        "bg-transparent",
        compact ? "p-0" : "px-5 pb-0 pt-4",
      )}
    >
      <div
        className={classNames(
          "w-full items-center",
          compact
            ? "flex justify-end"
            : "grid grid-cols-1 justify-items-center gap-x-4 gap-y-2 lg:grid-cols-[auto_auto] lg:justify-items-stretch",
        )}
      >
        {estimatedCost && (
          <div className="row-start-2 flex min-w-0 justify-center lg:col-start-1 lg:row-start-1">
            {estimatedCost}
          </div>
        )}
        <div className="row-start-1 flex w-full shrink-0 justify-center lg:col-start-2 lg:w-auto">
          {isPublic ? (
            <Button
              className="w-auto min-w-[250px] max-w-full justify-center"
              disabled={isDisabled}
              onClick={onDeploy}
            >
              {deployLabel}
            </Button>
          ) : (
            <div
              className={classNames(
                "w-auto max-w-full",
                !isDisabled ? "pulse-ready-button" : "",
              )}
            >
              <Button
                className="w-auto min-w-[250px] max-w-full justify-center"
                disabled={isDisabled}
                onClick={onDeploy}
              >
                {initLoading ? (
                  <span className="flex items-center gap-2">
                    <Spinner
                      type="secondary"
                      className="!text-black/40 !mr-0 !ml-0"
                    />
                    <span className="text-black/60">Launching...</span>
                  </span>
                ) : loading || instanceLoading ? (
                  "Loading..."
                ) : (
                  deployLabel
                )}
              </Button>
            </div>
          )}
        </div>
        {disclosure && (
          <div className="row-start-3 mt-2 max-w-[350px] pb-8 text-center text-xs leading-5 text-secondary lg:col-span-2 lg:col-start-1 lg:row-start-2 lg:justify-self-end lg:text-right">
            {disclosure}
          </div>
        )}
      </div>
    </div>
  );
};
